import { useRef, useState, type ReactNode } from "react";
import { ExternalLink, FileText, Loader2, MapPin, Zap } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { InlineEdit } from "@/components/InlineEdit";
import { AddContactModal } from "@/components/add/AddContactModal";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import { EntityRemindersButton } from "@/components/reminders/EntityRemindersButton";
import { useBidLines } from "@/components/tracker/DealEngagements";
import { useCurrentUserTimezone } from "@/hooks/useCurrentUserTimezone";
import { useCompanyField } from "@/hooks/useCompanyField";
import { useCompanySummary } from "@/hooks/useRecordSummary";
import { RecordFrame, RecordPanel } from "@/components/record/RecordFrame";
import { RecordHeader, RecordChip, type SublineItem } from "@/components/record/RecordHeader";
import { FactsStrip } from "@/components/record/FactsStrip";
import { ActionBar } from "@/components/record/ActionBar";
import { BriefPanel } from "@/components/record/BriefPanel";
import { ProcessesPanel } from "@/components/record/ProcessesPanel";
import { EditableTextPanel } from "@/components/record/EditableTextPanel";
import { CompanyRail } from "@/components/record/CompanyRail";
import { CompanyReach } from "@/components/record/CompanyReach";
import { companyLinkItems } from "@/components/record/LinkField";
import { CompanyNameEdit } from "@/components/record/CompanyNameEdit";
import { CompanyMoreMenu } from "@/components/record/CompanyMoreMenu";
import { ArchiveCompanyDialog } from "@/components/record/ArchiveCompanyDialog";
import { useRecordCompose } from "@/components/record/useRecordCompose";
import { buyerFacts } from "@/components/record/facts/companyFacts";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { downloadText, fullDay, localYmd, primaryContact, sourceLabel } from "@/components/record/recordFormat";
import type { CompanyData } from "./companyTypes";
import { BuyerCriteriaPanel } from "./buyer/BuyerCriteriaPanel";
import { BuyerDealsPanel } from "./buyer/BuyerDealsPanel";
import { BuyerEnrichReview } from "./buyer/BuyerEnrichReview";
import { PortfolioPanel } from "./buyer/PortfolioPanel";
import { useBuyerEnrichment } from "./buyer/useBuyerEnrichment";

export default function BuyerRecord({ company }: { company: CompanyData }) {
  const userTz = useCurrentUserTimezone();
  const { saveFieldAsync } = useCompanyField(company.id);
  const summary = useCompanySummary(company.id);
  const bids = useBidLines(company.id);
  const enrich = useBuyerEnrichment(company);
  const { compose, setCompose, openLog, nextStepOpen, setNextStepOpen } = useRecordCompose();
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const onePager = useRef<HTMLInputElement>(null);

  const people = company.contacts ?? [];
  const primary = primaryContact(people);
  const today = localYmd(new Date());
  const e = company.enrichmentData;
  const busy = enrich.phase === "website" || enrich.phase === "apollo";
  const funds = (e?.fundingHistory ?? []).map((f: any) => `${f.amount} (${f.year})`).join(", ");

  const subline: SublineItem[] = [
    { icon: <MapPin />, content: <InlineEdit label="Headquarters" value={company.headquarters} placeholder="City, State" addLabel="Add HQ" onSave={(v) => saveFieldAsync("headquarters", v.trim() || null)} /> },
    ...companyLinkItems({ website: company.website, linkedinUrl: company.linkedinUrl, onSave: (field, v) => saveFieldAsync(field, v) }),
    ...(e?.apolloId ? [{ icon: <ExternalLink />, content: <a href={`https://app.apollo.io/#/organizations/${e.apolloId}`} target="_blank" rel="noopener noreferrer" className="hover:underline">Apollo</a> }] : []),
    ...(e?.foundedYear ? [{ content: `Est. ${e.foundedYear}` }] : []),
  ];

  const header = (
    <RecordHeader
      crumb={{ label: "Buyer Network", href: "/buyers" }}
      current={company.name}
      name={<CompanyNameEdit value={company.name} onSave={(t) => saveFieldAsync("name", t)} />}
      chips={<>
        <RecordChip tone="category">{company.subType || "Buyer"}</RecordChip>
        {company.dualRoleBuyerCp && <RecordChip>Also capital provider</RecordChip>}
        {people.some((p) => p.engaged) && <RecordChip tone="good">Spoken with</RecordChip>}
      </>}
      subline={subline}
      reach={<CompanyReach people={people} mainPhone={e?.phone} />}
      actions={
        <ActionBar onLog={openLog} email={primary?.email} onNextStep={() => setNextStepOpen(true)}>
          <EntityRemindersButton entityType="company" entityId={company.id} entityName={company.name} userTimezone={userTz} />
          <Button size="sm" variant="outline" className="gap-1.5" onClick={enrich.runEnrich} disabled={busy || !company.website} title={company.website ? "Scan the firm's website" : "Add a website to enrich from it"}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {enrich.phase === "website" ? "Scanning…" : enrich.phase === "apollo" ? "Apollo…" : "Enrich"}
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onePager.current?.click()} disabled={busy} title="Read criteria from a one-pager PDF">
            <FileText className="h-3.5 w-3.5" />One-pager
          </Button>
          <input
            ref={onePager}
            type="file"
            accept=".pdf,image/*"
            className="hidden"
            onChange={(ev) => { const f = ev.target.files?.[0]; ev.target.value = ""; if (f) void enrich.readOnePager(f); }}
          />
          <CompanyMoreMenu company={company} onArchive={() => setArchiveOpen(true)}>
            <DropdownMenuItem onSelect={() => downloadText(`${company.name.replace(/\s+/g, "-")}.txt`, [`Company: ${company.name}`, `Type: ${company.subType || ""}`, `Industry: ${company.industry || ""}`, `Website: ${company.website || ""}`].join("\n"))}>Export</DropdownMenuItem>
          </CompanyMoreMenu>
        </ActionBar>
      }
    />
  );

  const work = (
    <>
      <BriefPanel
        summary={summary.data}
        loading={summary.isLoading}
        error={summary.isError}
        onRetry={() => summary.refetch()}
        openLabel={`Open on ${company.name}`}
        target={{ companyId: company.id }}
        nextStepOpen={nextStepOpen}
        onNextStepOpenChange={setNextStepOpen}
      />
      <BuyerEnrichReview enrich={enrich} company={company} />
      <BuyerCriteriaPanel company={company} saveCompanyFieldAsync={saveFieldAsync} />
      <EditableTextPanel
        title="Investment profile"
        value={company.description}
        onSave={(v) => saveFieldAsync("description", v.trim() || null)}
        emptyText="No description yet. Enrich from the firm's website to fill it in."
      />
      {summary.data?.processes && <ProcessesPanel rows={summary.data.processes} title="Processes" bids={bids} />}
      <BuyerDealsPanel companyId={company.id} />
      <PortfolioPanel companyId={company.id} />
      <RecordPanel id="record-activity">
        <ActivityTimeline
          scope={{ kind: "company", companyId: company.id, companyName: company.name, contacts: people }}
          compose={compose}
          onComposeChange={setCompose}
        />
      </RecordPanel>
    </>
  );

  const details: [string, ReactNode][] = [
    ...(e?.totalFunding ? [["Total capital", e.totalFunding] as [string, ReactNode]] : []),
    ...(funds ? [["Funds", funds] as [string, ReactNode]] : []),
    ["Added", fullDay(company.createdAt)],
    ["Source", sourceLabel(company.creationSource)],
    ["Added by", company.creator?.name ?? "Unknown"],
  ];

  return (
    <AppLayout title={company.name}>
      <RecordFrame
        header={header}
        facts={summary.data
          ? <FactsStrip facts={buyerFacts(summary.data, today, { lastActivityAt: company.lastActivityAt ?? null, criteria: company.acquisitionCriteria ?? null })} />
          : summary.isLoading ? <div className="h-[58px] animate-pulse rounded-lg border bg-card" /> : null}
        work={work}
        rail={
          <CompanyRail
            company={company}
            summary={summary}
            details={details}
            onSaveNotes={(v) => saveFieldAsync("notes", v.trim() || null)}
            onAddPerson={() => setAddPersonOpen(true)}
          />
        }
      />
      <AddContactModal open={addPersonOpen} onOpenChange={setAddPersonOpen} preselectedCompanyId={company.id} preselectedCompanyName={company.name} />
      <ArchiveCompanyDialog company={company} open={archiveOpen} onOpenChange={setArchiveOpen} />
    </AppLayout>
  );
}
